package org.multiplatform.kotfolio.pages.compose.architecture

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.components.WebImage
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import org.multiplatform.kotfolio.util.Resources
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul

@Page("/compose/architecture/composition")
@Composable
fun CompositionArchitectureComposePage() {
    BlogLayout(
        PageProperties(
            "Composition Architecture",
            "Jetpack Compose is a compiler plugin and a runtime that let you build and manage tree-like structures and their state."
        ),
        BlogProperties("Jetpack Compose", "Composition Architecture")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Jetpack Compose is a compiler plugin and a runtime that let you build and manage tree-like structures and their state. The runtime provides the engine for composable functions, and the compiler plugin rewrites your code so the runtime can understand and manage it.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Compose runtime uses an internal data structure called the SlotTable to store composition data. Although the SlotTable holds a tree structure, its payload is flattened into two arrays to reduce runtime memory allocations. In the old View-based framework, developers could attach a debugger to investigate the View tree structure. However, with Compose, debugging the UI tree has become much more challenging due to its reliance on internal data structures like the SlotTable. A tool that provides a visual representation of the composition tree within a user-friendly UI would be invaluable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }
            }

            SpanText(
                text = "What do we mean by “runtime”?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Think of the runtime as the engine that powers all composable logic.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It handles:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Tracking State changes",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Remembering values across recompositions (remember()).",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    
                            )
                        }

                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Coordinating updates and re-entries of composable scopes.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    
                            )
                        }
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Essentially, it provides the “machinery” that makes @Composable functions possible.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }
            }

            SpanText(
                text = "Composition is a object that manages very important components in Compose Framework.",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Composition has SlotTable which is data structure for ui node tree.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Composition has Composer which makes updates on SlotTable if there are changes in Compose State.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Composition has Applier which renders the ui.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }
            }

            SpanText(
                text = "Components of Composition",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            WebImage(
                src = Resources.Images.COMPOSITION_ARCHI_1,
                modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                contentDescription = "Components of Composition"
            )

            SpanText(
                text = "SlotTable - (Linear, Positionally Indexed Structure)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Slot Table is Compose's internal representation of your UI hierarchy. It’s not a tree of View objects like in the traditional system. Instead, it’s a linear data structure that keeps track of which Composable functions are currently in the UI and their relationship to each other. This allows for very efficient updates.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)

                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Instead, Compose relies on a single, flat, linear data structure called the Slot Table.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Slot Table acts as an execution ledger. It stores data sequentially using an array-backed structure conceptually inspired by a Gap Buffer (the same structure text editors use to handle cursor insertions). As the Composer executes your Composable functions, it moves sequentially through the table, reading past data and inserting new data where the \"gap\" is positioned.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This mechanism relies heavily on Positional Memoization — the execution context of a Composable is bound directly to where it is called in the code, rather than just an arbitrary object identity.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose’s memory book that matches old UI with new UI. When your code runs, the \$composer is \"writing\" into this table. This is why structural stability is vital. If you use a conditional branch (if/else) that adds or removes composables, the Slot Table \"shifts.\" This is why conditionals must be stable or use key() to help the compiler identify elements that have moved rather than disappeared. When you use remember { ... }, Compose doesn't just store a value; it stores it at a specific \"address\" in the Slot Table. The \"identity\" of your state is tied to where it was called in the execution flow.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "SlotTable is data structure for current state of Composition, like composable function parameters, remembered values, CompositionLocals, etc. SlotTable manages Composition state using two core concept: groupand slot",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                KotlinCode(
                    code = """
                         internal class SlotTable : CompositionData, Iterable<CompositionGroup> {
                         var groups = IntArray(0)
                            private set

                         var slots = Array<Any?>(0) { null }
                             private set
                    """.trimIndent()
                )

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Group describes how to interpret the slot array. Each group has 5 group fields which is just a bit that has its own meaning.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The SlotTable can be thought of as a list that’s typically larger than the data it contains, leaving empty spaces known as gap buffers. It uses a flat list with gap buffers to efficiently insert, remove, or skip slots without shifting everything. Slot represents the actual data for each Composable belonged to the group. It can be any type of informations as you can see its type:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                KotlinCode(
                    code = """
                    var slots = Array<Any?>(0) { null }
                        private set
                    """.trimIndent()
                )

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Slot space is a linear array, starting point of which is addressed by “group”. Each slot space is implemented as Gap Buffer for efficient operation. SlotTable relies on the Snapshot system to know when to update, keeping apps fast and smooth.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Slot space is a linear array, starting point of which is addressed by “group”. Each slot space is implemented as Gap Buffer for efficient operation.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose uses a Slot Table - a linear runtime memory structure.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Records composable call order.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Stores remembered values and state reads.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Matches previous vs new composition by position.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    
                            )
                        }
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "On state change:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Only slots that read that state are invalidated.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Unchanged slots are skipped.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Enables precise partial recomposition.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    
                            )
                        }
                    }
                }
            }

            SpanText(
                text = "SlotTable 3 primary elements",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Groups: Structural markers recording the control flow and positional hierarchy of the UI.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Slots: The actual data payload (remember variables, parameters, and state object references).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)

                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Anchors: Stable pointers tracking positions as they shift within the table across subsequent compositions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)

                    )
                }
            }
            
            SpanText(
                text = "Compose Runtime",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose is called a runtime because it actively tracks state and decides, at runtime, which parts of the UI need to be recomposed and updated. That’s the magic behind its efficiency.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }
            }

            WebImage(
                src = Resources.Images.COMPOSITION_ARCHI_2,
                modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                contentDescription = "Slot table"
            )

            SpanText(
                text = "Compose uses a flat array with a gap buffer.",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "While you can’t view the raw table at runtime, the Layout Inspector in Android Studio effectively walks this data structure to show you exactly which Composables are active and what state they hold.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Flat array: keeps slots sequential in memory, making iteration cache-friendly and fast.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Gap buffer: a movable empty space within the array that allows fast insertions and deletions. Instead of shifting the entire array when nodes are added or removed, Compose just moves the gap and writes into it, cutting down memory copying and CPU overhead.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }
            }

            SpanText(
                text = "How does SlotTable actually store nodes?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Each slot in the SlotTable acts as a compact record tying a composable node to its state and metadata:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A unique hash that identifies the composable. This lets Compose know which UI element we’re talking about when recomposing.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Metadata: Stores details about a composable — its parameters, configuration, and links to child slots or state — so Compose knows how to handle and update it.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State pointers: references to remember, rememberSaveable, or mutableStateOf values. This is crucial because the slot needs to know which state objects drive its UI updates.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Anchors: Keep track of where a slot was in the previous composition, letting Compose reuse nodes and their state even if the UI tree changes, minimising unnecessary recompositions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }
            }

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "With this layout, Compose can handle dynamic UIs with lots of composables efficiently. But storing slots isn’t enough.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose also needs to know which parts of the UI need updates when state changes. That’s where anchors and state pointers come in.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Anchors connect each slot to a previous slot, allowing Compose to map the current UI tree to the prior SlotTable. This makes selective recomposition possible: only slots whose state changed are recomposed.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "State pointers directly link slots to remembered state objects, so Compose knows exactly which UI elements depend on which state.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                
                        )
                    }
                }
            }

            SpanText(
                text = "Composer",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When you compile Composable function, Compose Compiler Plugin changes all of Composable function. It adds Composer on function parameter in IR stage of compile like suspend function with Continuation.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Composer creates groups or slots on SlotTable for each of Composable. Composer also “emits” LayoutNode through Applier. You can see how composer create group & slot on ResuableComposeNode , which is foundation component of Compose system.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }
            }

            SpanText(
                text = "Applier (Ui Node Tree)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Applier is simple interface that is defined in compose runtime library. Applier implementation in each of platform ui libraries(like androidx.compose.ui) implements tree operation for ui node tree. UI rendering is delegated to LayoutNode of compose ui in case of Android.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Each Composable becomes LayoutNode in ui tree. LayoutNode forms its drawing layers, which include materialization of Modifier chain.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Each Composable function is materialized to LayoutNode , and it forms NodeCoordinator chain internally. Basically the chain is a linked list. Each of Modifier is converted to LayoutModifierNodeCoordinator in NodeChain",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }
            }

            SpanText(
                text = "Runtime work",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "SlotTable: In-memory data structure that keeps track of groups and nodes — a flattened representation of the current composition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Nodes: Abstract entities representing entries within the SlotTable. Some of these are NodeGroups, which correspond to tangible nodes managed by an Applier.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Applier: The mechanism that translates NodeGroups into a materialized tree. It is the actual use case that leverages the compose runtime (UI, DI, JSON, etc.).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Composition: The scope that defines how the SlotTable and Applier work together for a given composable hierarchy.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Recomposer: The coordinator that observes state changes and schedules recompositions at the right time.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "MonotonicFrameClock: The clock that drives when recompositions occur — effectively the “game loop” of Compose. On Android, it’s tied to the Choreographer which schedules updates once per VSYNC frame (typically every 16.6 ms at 60 Hz, or faster on 90 Hz/120 Hz displays), while in frameworks like Molecule, it runs as fast as possible to emit continuous state updates.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }
            }

            SpanText(
                text = "Snapshot System",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose uses a snapshot system to manage state changes and trigger recompositions. When a MutableState’s value changes, it creates a “snapshot” of the current state. The Compose runtime then observes these snapshots and knows exactly which Composables need to be re-evaluated. This is far more granular than how Android's traditional observer pattern might work. When a state change occurs, the runtime compares the current Slot Table with the expected state based on the new snapshot. It then identifies the minimal set of Composables that need to be recomposed.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "An MVCC-inspired transactional state engine that tracks property reads and isolates writes, eliminating global memory locks.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)

                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Snapshot state provides transactional, thread-safe observation of mutable state (MutableState) — changes are recorded in a snapshot and only become globally visible when applied, enabling Compose to track fine-grained reads/writes for efficient recomposition without locks on the read path.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Slot Table handles structural history, but tracking when a state property changes falls to the Snapshot System.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)

                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When you call mutableStateOf(), you are not just creating a variable. You are creating a state object that participates in Compose's snapshot system — a mechanism borrowed from database MVCC concepts that provides transactional isolation, concurrent read safety, and automatic change observation. This explains why Compose state is safe across threads, why reads are automatically tracked, and how the runtime knows exactly which composables to invalidate when something changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Snapshot System is Compose’s internal mechanism for safely tracking and applying state changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose’s state is managed by the Snapshot System, and all state read and write operations are tracked per snapshot.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Snapshot System detects state changes and informs the Compose Runtime that the composable (the scope of execution for a Composable function) that read that state must be re-executed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When you use mutableStateOf in Compose, you’re not just holding a plain value. Behind the scenes, Compose wraps it inside the Snapshot system.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It captures the “current state” during composition, and batches writes/updates to avoid flickers or inconsistent UI.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State writes are done inside transactions. Multiple updates can be grouped and applied atomically. This ensures smoothness and consistency.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose’s snapshot system also ensures thread safety when managing state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Ol {
                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "On read",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    .fontWeight(FontWeight.Bold)
                                    
                            )
                        }

                        Ul {
                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "When state is read, the read operation must be registered in the global snapshot. When your composable reads a state (say count.value), Compose records that “this composable cares about count.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                        
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "When a composable reads a State value during composition, the getter calls the current snapshot's read observer, passing a reference to itself. The read observer records this relationship: 'the composable scope currently executing has read this specific state object.'",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                        
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "By the end of composition, Compose has built a complete map of which composable scopes depend on which state objects — the read set. When a state object changes, only the scopes in its read set are scheduled to recompose. Everything else in your UI is completely untouched.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                        
                                )
                            }
                        }
                    }

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "On write",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    .fontWeight(FontWeight.Bold)
                                    
                            )
                        }

                        Ul {
                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "When you update that state (count.value++), Compose doesn’t blindly recompose everything. It checks if the value actually changed. If it did, only the composable that previously read that state are scheduled for recomposition.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                        
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "Writing to a MutableState does not immediately update the global state. The setter writes a new value to a local, per-snapshot record. Other snapshots — including the composition snapshot — cannot see this change yet. When the mutable snapshot is applied, the new values are merged into the global snapshot, write observers are notified, and the Recomposer schedules only the scopes in those states' read sets.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                        
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "This mechanism is why Compose can do targeted recomposition instead of redrawing your whole UI tree.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                        
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "On top of that, snapshots make all of this thread-safe. Multiple reads/writes can happen in parallel, but every composable sees a consistent view of the state — almost like how transactions work in a database.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                        
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "That’s it. That’s the Snapshot system, and it’s what connects your State objects to recomposition.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                        
                                )
                            }
                        }
                    }

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Notification Loop",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    .fontWeight(FontWeight.Bold)

                            )
                        }

                        Ul {
                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = " Once committed, a global write observer notifies the Recomposer. The system then efficiently resumes traversal at the invalidated group using stored Anchors, rather than performing an expensive lookup across the entire layout tree.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)

                                )
                            }
                        }
                    }
                }
            }

            SpanText(
                text = "Why Use Snapshot.takeSnapshot()?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The primary purpose of Snapshot.takeSnapshot() is to create a read-only view of the current state. This is particularly useful when:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Debugging or analyzing the current state without affecting its values.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Performing calculations or temporary operations based on the current state.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Ensuring thread safety when reading state values in a multithreaded environment.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                
                        )
                    }
                }
            }

            KotlinCode(
                code = $$"""
                    import androidx.compose.runtime.MutableState
                    import androidx.compose.runtime.mutableStateOf
                    import androidx.compose.runtime.snapshots.Snapshot

                    class User {
                        var name: MutableState<String> = mutableStateOf("")
                    }

                    fun main() {
                        val user = User()

                        // Set the initial name
                        user.name.value = "Kamlesh"

                        // Take a snapshot of the current state
                        val snapshot = Snapshot.takeSnapshot()

                        // Modify the state after taking the snapshot
                        user.name.value = "Android"

                        println("Current name: ${user.name.value}") 
                        // Output: Android

                        // Enter the snapshot to read the captured value
                        snapshot.enter {
                            println("Snapshot name: ${user.name.value}")
                            // Output: Kamlesh
                        }

                        // Dispose the snapshot
                        snapshot.dispose()
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Explanation of the Example",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Initial State: The User class has a name property backed by MutableState. The initial value is set to \"kamlesh\".",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Take Snapshot: A snapshot is taken using Snapshot.takeSnapshot(). At this moment, the name value is frozen as \"kamlesh\".",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Modify State: After taking the snapshot, the name is updated to \"Android\". This change does not affect the snapshot.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Enter Snapshot: The enter function restores the captured state temporarily within its block. Inside the block, the name is still \"kamlesh\".",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Dispose Snapshot: The snapshot is disposed of to free resources.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }
            }

            SpanText(
                text = "Key Features of Snapshot.takeSnapshot()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Read-Only: Snapshots taken using Snapshot.takeSnapshot() are strictly read-only. Any attempt to modify state inside the snapshot will result in an IllegalStateException.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Thread Safety: By freezing the state, snapshots ensure that you can safely read values without worrying about concurrent modifications.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Isolation: State changes made outside the snapshot do not affect the captured state. Similarly, the snapshot does not affect the current state of the program.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }
            }

            SpanText(
                text = "When Does Recomposition Happen?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When a state read during composition changes (e.g. a mutableStateOf or collectAsState(...) value)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When the parameters passed to a composable change (new object references or values)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If parent composable is invalidated (e.g., it reads some state and that state changed), the parent re-runs; children may or may not be re-run depending on whether their inputs changed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }
            }

            SpanText(
                text = "What really happens inside Jetpack Compose when your UI recomposes?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            WebImage(
                src = Resources.Images.COMPOSE_RUNTIME_RE_COMPOSE_URL,
                modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                contentDescription = "Runtime recomposes"
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Composer — The Brain of Compose",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )

                    Ul {
                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Every aComposable you write secretly talks to something called the Composer. It tracks:",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    
                            )

                            Ol {
                                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                    SpanText(
                                        text = "Which UI is being built",
                                        modifier = Modifier
                                            .fontSize(FontSize.Medium)
                                            
                                    )
                                }

                                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                    SpanText(
                                        text = "Which UI is being built",
                                        modifier = Modifier
                                            .fontSize(FontSize.Medium)
                                            
                                    )
                                }

                                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                    SpanText(
                                        text = "When to reuse or skip recomposition",
                                        modifier = Modifier
                                            .fontSize(FontSize.Medium)
                                            
                                    )
                                }
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "Think of it as the “brain” connecting your function calls to the actual UI tree.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                        
                                )
                            }
                        }
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "remember() — The Memory System",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )

                    Ul {
                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "This little function is basically Compose’s built-in cache. f userId doesn’t change, Compose skips the expensive work and reuses the value.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    
                            )

                            Ol {
                                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                    SpanText(
                                        text = "It checks if any key changed (changed() method).",
                                        modifier = Modifier
                                            .fontSize(FontSize.Medium)
                                            
                                    )
                                }

                                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                    SpanText(
                                        text = "If yes → recompute.",
                                        modifier = Modifier
                                            .fontSize(FontSize.Medium)
                                            
                                    )
                                }

                                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                    SpanText(
                                        text = "If no → reuse cached value.",
                                        modifier = Modifier
                                            .fontSize(FontSize.Medium)
                                            
                                    )
                                }
                            }
                        }
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "key() — Identity Management",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )

                    Ul {
                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "When you map lists without key, Compose might reuse state incorrectly.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    
                            )
                        }
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ComposeNode() — Where UI Nodes Are Born",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )

                    Ul {
                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Every composable eventually becomes a node.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    
                            )

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "ComposeNode is the bridge between the Composer (logic) and the Applier (actual UI).",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                        
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "It decides when to create, reuse, or update nodes in the tree. This is the secret sauce that lets Compose skip 90% of UI work efficiently.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                        
                                )
                            }
                        }
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Reusable Content = RecyclerView Energy",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )

                    Ul {
                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "ReusableContent(key) allows Compose to recycle nodes instead of recreating them.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    
                            )

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "Perfect for list-heavy UIs or expensive trees — like your own custom recyclers.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                        
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "It separates state reuse (via remember) from node reuse (via ReusableContent).",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                        
                                )
                            }
                        }
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Positional Memory Model",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )

                    Ul {
                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Compose doesn’t remember by variable name — it remembers by position in code.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    
                            )

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "That’s why moving code or missing keys can break state unexpectedly.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                        
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "Your function’s position defines identity. That’s also why tools like key() are crucial for stability.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                        
                                )
                            }
                        }
                    }
                }
            }

            SpanText(
                text = "Recomposer",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Recomposer is the central coordinator that ties all other systems together. It listens for invalidation signals from the snapshot system, manages a queue of composable scopes that need to re-run, synchronises with the display's frame rate via the Choreographer, and drives the actual recomposition pass. Understanding the Recomposer explains the timing of when composables re-run, how Compose batches state changes, and why multiple state writes within a single frame resolve in one recomposition pass.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }
            }

            SpanText(
                text = "One Recomposer Per Composition Root",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In a typical Android app, one Recomposer is created per setContent{} call (or ComposeView). It owns a coroutine scope backed by the main thread dispatcher. All composition for that root runs through this single Recomposer — which is why Compose batches all state changes that happen between two vsync signals into a single recomposition pass.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }
            }

            SpanText(
                text = "Recomposition Pipeline",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State change detected: A MutableState value is written anywhere in the app. The mutable snapshot's write observer records which State objects were touched.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Snapshot applied, observers notified: When the mutable snapshot is applied, the global snapshot is updated and observers are called with the set of changed state objects. The Recomposer adds affected RecomposeScopes to its invalidScopes queue.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Frame requested: The Recomposer transitions from Idle to PendingWork and requests a frame from the BroadcastFrameClock. On Android this delegates to the Choreographer at the display's refresh rate (60 or 120 Hz). Every state change between two vsync signals is batched together.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Recompose pass: On the next vsync, all invalid scopes are dequeued and re-executed inside a new mutable snapshot so reads are freshly tracked and writes are isolated.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Apply changes: The Applier flushes structural changes from the slot table into the actual UI node tree. Only nodes that changed during composition are touched.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }
            }

            SpanText(
                text = "Conclusion",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Composer updates SlotTable and notify ui changes to Applier",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "SlotTable is data structure for composition state",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Applier delegates rendering to UI Node.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Snapshot system is the secret behind Compose’s efficient state management. It ties directly into the SlotTable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            
                    )
                }
            }
        }
    }
}

//https://proandroiddev.com/compose-for-di-35b44ba4099b (Slot Table)
//https://medium.com/@sivavishnu0705/83fe7ad12494
//https://medium.com/@hyunjine/compose-internal-5-compose-ui-2-41d0cee927d4